import React from "react";
// Import removed as it's not used in this component

type BasicInfoProps = {
	formData: {
		title: string;
		slug: string;
		description: string;
	};
	handleInputChange: (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => void;
	isEditingSlug: boolean;
	toggleSlugEditMode: () => void;
	regenerateSlug: () => void;
	slugCheckStatus: "idle" | "checking" | "available" | "taken" | "error";
	slugCheckMessage: string | null;
	slugCompletionDisabled: boolean;
};

const BasicInfo: React.FC<BasicInfoProps> = ({
	formData,
	handleInputChange,
	isEditingSlug,
	toggleSlugEditMode,
	regenerateSlug,
	slugCheckStatus,
	slugCheckMessage,
	slugCompletionDisabled,
}) => {
	return (
		<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
			<h3 className="text-lg font-semibold text-gray-900 dark:text-white pb-2">
				基本情報
			</h3>
			<div className="space-y-4">
				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
						イベント名 <span className="text-red-500 dark:text-red-400">*</span>
					</label>
					<input
						type="text"
						name="title"
						value={formData.title}
						onChange={handleInputChange}
						maxLength={100}
						required
						className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 transition-colors"
					/>
					<div className="mt-1 flex items-center">
						<div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
							<div
								className="bg-gradient-to-r from-violet-500 to-purple-600 h-2.5 rounded-full transition-all duration-300"
								style={{ width: `${(formData.title.length / 100) * 100}%` }}
							></div>
						</div>
						<p className="text-xs text-gray-500 dark:text-gray-400 ml-2 whitespace-nowrap">
							{formData.title.length}/100文字
						</p>
					</div>
				</div>

				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
						スラッグ（任意）
					</label>
					<div className="flex items-center">
						{isEditingSlug ? (
							<input
								type="text"
								name="slug"
								value={formData.slug}
								onChange={handleInputChange}
								className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 transition-colors"
							/>
						) : (
							<div className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white">
								{formData.slug}
							</div>
						)}
						<button
							type="button"
							onClick={toggleSlugEditMode}
							disabled={slugCompletionDisabled}
							className={`ml-2 px-3 sm:px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
								slugCompletionDisabled
									? "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed"
									: "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
							}`}
						>
							{isEditingSlug ? "完了" : "編集"}
						</button>
						<button
							type="button"
							onClick={regenerateSlug}
							className="ml-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-lg hover:from-violet-600 hover:to-purple-700 whitespace-nowrap transition-all"
						>
							再生成
						</button>
					</div>
					<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
						英数字8文字のスラッグが自動生成されます
					</p>
					{slugCheckStatus === "checking" && (
						<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
							{slugCheckMessage || "スラッグを確認しています..."}
						</p>
					)}
					{slugCheckStatus === "taken" && (
						<p className="text-xs text-red-500 dark:text-red-400 mt-1">
							{slugCheckMessage || "このスラッグは既に使用されています"}
						</p>
					)}
					{slugCheckStatus === "available" && (
						<p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
							{slugCheckMessage || "このスラッグは使用できます"}
						</p>
					)}
					{slugCheckStatus === "error" && (
						<p className="text-xs text-red-500 dark:text-red-400 mt-1">
							{slugCheckMessage || "スラッグの確認に失敗しました"}
						</p>
					)}
				</div>

				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
						説明
					</label>
					<textarea
						name="description"
						value={formData.description}
						onChange={handleInputChange}
						maxLength={3000}
						rows={6}
						className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 transition-colors resize-none"
					/>
					<div className="mt-1 flex items-center">
						<div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
							<div
								className="bg-gradient-to-r from-violet-500 to-purple-600 h-2.5 rounded-full transition-all duration-300"
								style={{
									width: `${(formData.description.length / 3000) * 100}%`,
								}}
							></div>
						</div>
						<p className="text-xs text-gray-500 dark:text-gray-400 ml-2 whitespace-nowrap">
							{formData.description.length}/3000文字
						</p>
					</div>
				</div>
			</div>
		</div>
	);
};

export default BasicInfo;
